import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CargoTable from "../components/CargoTable";
import { Container, Form, Button, Card } from "react-bootstrap";
import { editcargo } from "../../../services/cargoService";
import { getListaCargoDocumentType } from "../../../services/cargoDocumentTypeService";
import { getUiInterface, getPageName, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import AlertOverlay from "../../../components/Alert";

interface Document {
  id: number;
  key: string;
}

type ParamType = {
  id: string;
};

const StepTwoEditView: React.FC = () => {
  const location = useLocation();
  const formData = location.state?.formData || {};
  const { id } = useParams<ParamType>();
  const [cargoDocumentTypeList, setCargoDocumentTypeList] = useState<
    Document[]
  >([]);
  const [invoice, setInvoice] = useState(formData.Invoice || "");
  const [documentTypeId, setDocumentTypeId] = useState(
    formData.DocumentTypeId || ""
  );
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingDocuments] = useState<any[]>(
    formData.CargoContents || []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState<{
    message: string;
    variant: "success" | "danger" | "warning" | "info";
  } | null>(null);

  const formatDate = (dateString: string | Date): string => {
    if (!dateString) return "";
    if (dateString instanceof Date) return dateString.toISOString();
    return new Date(dateString).toISOString();
  };

  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getListaCargoDocumentType();
        setCargoDocumentTypeList(response.values || []);
      } catch (error) {
        console.error("Erro ao buscar documentos:", error);
      }
    };
    fetchData();
  }, []);

  const navigate = useNavigate();

  function normalizeDecimalForApi(value: number | string) {
    const raw = typeof value === "number" ? value.toString() : String(value);
    let s = raw.trim();
    if (!s) return s;
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma !== -1 && lastDot !== -1) {
      if (lastComma > lastDot) {
        s = s.replace(/\./g, '');
        s = s.replace(/,/g, '.');
      } else {
        s = s.replace(/,/g, '');
      }
    } else if (lastComma !== -1) {
      s = s.replace(/,/g, '.');
    }
    return s;
  }

  const handleSubmit = async () => {
    setIsSubmitting(true);
  const nullRequiredFields: string[] = [];
  
  // Validação: precisa ter documentos existentes ou novos selecionados
  const hasDocuments = existingDocuments.length > 0 || selectedFiles.length > 0;
  if (!hasDocuments) nullRequiredFields.push('Content');
  if (!formData.Invoice) nullRequiredFields.push('Invoice');
  if (!formData.DocumentTypeId) nullRequiredFields.push('DocumentTypeId');
      
      if (nullRequiredFields.length > 0) {
          setInvalidFields(new Set(nullRequiredFields));
          const warnMessage: string =
            nullRequiredFields.length === 1
              ? getUiInterface('fieldRequiredSingle', {
                  field: nullRequiredFields[0],
                })
              : getUiInterface('fieldRequiredMultiple', {
                  fields: `${nullRequiredFields.slice(0, -1).join(', ')}${
                    nullRequiredFields.length > 1
                      ? ` ${getWordConnector('and')} `
                      : ''
                  }${nullRequiredFields.slice(-1)}`,
                });
    
          setAlert({ message: warnMessage, variant: 'danger' });
          setIsSubmitting(false);
          return;
    }

    try {
      const payload = new FormData();
      payload.append(
        "CargoId",
        String(formData.CargoId || formData.Cargo?.id || "")
      );
      
      formData.CargoContentId && ( payload.append(
        "CargoContentId",
        String(formData.CargoContentId || formData.CargoContent?.id || null)
      ))

      payload.append(
        "CargoContentCargoId",
        String(
          formData.CargoContentCargoId || formData.CargoContent?.cargoId || ""
        )
      );
      payload.append("Id", String(id));
      payload.append(
        "FromId",
        String(formData.FromId || formData.from?.id || "")
      );
      payload.append(
        "ClientId",
        String(formData.ClientId || formData.client?.id || "")
      );
      payload.append(
        "SupplierId",
        String(formData.SupplierId || formData.supplier?.id || "")
      );

      if (formData.Pu) {
        payload.append("Pu", formatDate(formData.Pu));
      }
      if (formData.ArrivalDate) {
        payload.append("ArrivalDate", formatDate(formData.ArrivalDate));
      }

      payload.append("Dawb", formData.Dawb || "");
      payload.append("Dgr", String(formData.Dgr || false));
      payload.append("DgrGoods", String(formData.DgrGoods || false));
      payload.append("SoNumber", formData.SoNumber || "");
      payload.append("WrNumber", formData.WrNumber || "");
      payload.append("Invoice", invoice);
      payload.append("DocumentTypeId", documentTypeId);
      payload.append(
        "Description",
        formData.Remarks || formData.description || formData.Description || ""
      );
      payload.append(
        "PoNumber",
        formData.PoRo || formData.poNumber || formData.PoNumber || ""
      );
      const weightRawValue = String(formData.Wgt || formData.weight || formData.Weight || 0);
      payload.append('Weight', normalizeDecimalForApi(weightRawValue));
      payload.append('WeightRaw', weightRawValue);
      payload.append(
        "Pieces",
        String(
          formData.QuantityOfPieces || formData.pieces || formData.Pieces || 0
        )
      );

      const dimensions = formData.Dimensions || formData.dimensions || [];
      dimensions.forEach((dimension: any, index: number) => {
        payload.append(
          `Dimensions[${index}].id`,
          String(dimension.id || dimension.Id || "")
        );
        payload.append(
          `Dimensions[${index}].quantity`,
          String(dimension.quantity || dimension.Quantity || 0)
        );
        payload.append(
          `Dimensions[${index}].Length`,
          normalizeDecimalForApi(dimension.length)
        );
        payload.append(
          `Dimensions[${index}].Width`,
          normalizeDecimalForApi(dimension.width)
        );
        payload.append(
          `Dimensions[${index}].Height`,
          normalizeDecimalForApi(dimension.height)
        );
      });
      
      // Usa o primeiro cargoContent existente para referência (se existir)
      const cargoContents = formData.CargoContents || formData.cargoContents || [];
      const firstCargoContent = cargoContents[0];
      
      if (firstCargoContent) {
        payload.append(
          "CargoContentId",
          String(firstCargoContent.id || firstCargoContent.Id || "")
        );
        payload.append(
          "CargoContentCargoId",
          String(firstCargoContent.cargoId || firstCargoContent.CargoId || "")
        );
        payload.append(
          "CargoContentContentId",
          String(firstCargoContent.contentId || firstCargoContent.ContentId || "")
        );
      }
      
      // Adiciona novos arquivos selecionados
      selectedFiles.forEach((file) => {
        payload.append("Content", file);
      });

      const response = await editcargo(Number(id), payload);

      if (response === 200 || response === 201) {
        setAlert({
          message: getSuccessMessage("editInfoCargo", { id: formData.id}),
          variant: "success",
        });
        setTimeout(() => {
        navigate("/cargo-info");
      }, 1000);
      } else {
        setAlert({
          message: getErrorMessage("editCargoError"),
          variant: "danger",
        });
      }
    } catch (error: any) {
        setAlert({
          message: getErrorMessage("editCargoError"),
          variant: "danger",
        });
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDocChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDocumentTypeId(e.target.value);
  };

  const handleInvoiceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInvoice(e.target.value);
  };

  // Compartilhada pelo <input type="file"> e pelo drop de arquivos (E4): as
  // duas formas de escolher arquivo caem no mesmo acúmulo em selectedFiles.
  const processFiles = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
    // Reset input para permitir selecionar os mesmos arquivos novamente
    e.target.value = '';
  };

  // E4: arraste de múltiplos arquivos direto na lista de documentos de envio.
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    processFiles(e.dataTransfer.files);
  };

  const handleRemoveFile = (fileName: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handleReturnPage = () => {
    navigate(`/cargo-info/cargo/${id}/edit`, { state: { formData } });
  };

  const rows = [{ invoice: invoice, document: documentTypeId, id: 1 }];
  const columns = [
    { field: "invoice", headerName: "Invoice", width: 150 },
    { field: "document", headerName: "Document Type", width: 150 },
  ];

  return (
    <Container
      fluid
      className="d-flex justify-content-center align-items-center vh-100"
      style={{ backgroundColor: "#f5f5f5" }}
    >
      {alert && (
            <AlertOverlay
                message={alert.message}
                variant={alert.variant}
                onClose={() => setAlert(null)}
            />
        )}
      <Card
        style={{
          width: "50%",
          padding: "20px",
          backgroundColor: "rgba(255, 255, 255, 0.34)",
          borderRadius: "10px",
        }}
      >
        <Card
          style={{
            width: "100%",
            padding: "20px",
            backgroundColor: "rgba(151, 146, 146, 0.29)",
            borderRadius: "10px",
          }}
        >
          <h2>{getPageName("invoiceList")}</h2>

          <Form>
            <Form.Group controlId="invoiceInput" className="mb-3">
              <Form.Label>Invoice</Form.Label>
              <Form.Control
                type="text"
                value={invoice}
                onChange={handleInvoiceChange}
                isInvalid={invalidFields.has('From')}
              />
            </Form.Group>

            <Form.Group controlId="docSelect" className="mb-3">
              <Form.Label>Document Type</Form.Label>
              <Form.Select value={documentTypeId} onChange={handleDocChange}>
                <option value="">{getUiInterface("selectAnDoc")}</option>
                {cargoDocumentTypeList?.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.key}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Documentos existentes */}
            {existingDocuments.length > 0 && selectedFiles.length === 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Documentos Cadastrados</Form.Label>
                <ul className="list-unstyled">
                  {existingDocuments.map((doc, index) => (
                    <li key={index} className="d-flex align-items-center gap-2 mb-1 p-2" style={{ backgroundColor: '#e9ecef', borderRadius: '4px' }}>
                      <span>{doc.content?.description || `Documento ${index + 1}`}</span>
                    </li>
                  ))}
                </ul>
              </Form.Group>
            )}

            <Form.Group controlId="fileUpload" className="mb-3">
              <Form.Label>
                {existingDocuments.length > 0 ? 'Substituir Documentos' : 'Upload Documentos'}
              </Form.Label>
              {existingDocuments.length > 0 && (
                <small className="d-block mb-2">
                  ⚠️ Adicionar novos arquivos irá substituir os documentos existentes.
                </small>
              )}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  border: '2px dashed',
                  borderColor: isDraggingFile ? '#0d6efd' : 'transparent',
                  backgroundColor: isDraggingFile ? 'rgba(13, 110, 253, 0.06)' : 'transparent',
                  borderRadius: 6,
                  padding: 8,
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}
              >
                <Form.Control type="file" multiple onChange={handleFileChange} />
                <small className="text-muted">{getUiInterface('orDragFilesHere')}</small>
                {selectedFiles.length > 0 && (
                  <div className="mt-2">
                    <p className="text-muted mb-1">Novos arquivos selecionados:</p>
                    <ul className="list-unstyled mb-0">
                      {selectedFiles.map((file, index) => (
                        <li key={index} className="d-flex align-items-center gap-2 mb-1">
                          <span>{file.name}</span>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveFile(file.name)}
                          >
                            X
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Form.Group>
          </Form>
        </Card>

        <Card
          style={{
            width: "100%",
            height: "10%",
            backgroundColor: "rgba(255, 255, 255, 0.34)",
          }}
        >
          <CargoTable rows={rows} columns={columns} width="100%" height={150} />
        </Card>

        <div className="d-flex justify-content-end gap-3 mt-3">
          <Button variant="danger" className="w-20" onClick={handleReturnPage}>
            {getUiInterface("return")}
          </Button>
          <Button
            variant="primary"
            className="w-20"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {getUiInterface("save")}
          </Button>
        </div>
      </Card>
    </Container>
  );
};

export default StepTwoEditView;
